import { Component, ElementRef, Input, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { ID_NUMBER_MASK, ID_NUMBER_REGEX, NON_DIGIT_AND_PLUS, PHONE_MASK, PHONE_REGEX, toProperCase } from "@modules/common";
import { ContractDto, CustomerMasterDto, JobSearchDto, MachineMasterDto } from "@shared/models";
import { Observable, of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";
import { trackEvent } from "src/util";
import { AppQuery, LoginStatus } from "../../common/app.store";
import { BaseFormDirective, TableColumn } from "../../common/components";
import {
  CustomerService,
  JobService,
  ProductSettingService,
  WebLayoutService
} from "../../common/services";

export type RegTypes = "job" | "registration";
export const REG_TYPES: Record<string, RegTypes> = {
  JOB: "job",
  REGISTRATION: "registration"
};

@Component({
  templateUrl: "./search.component.html",
  styleUrls: ["./search.component.scss"],
  selector: "abi-jobs-search"
})
export class SearchComponent extends BaseFormDirective implements OnInit {
  @Input() type: RegTypes = 'job';
  /**
   * Route to register a new customer
   */
  @Input() registerRoute = 'register';
  // eslint-disable-next-line @typescript-eslint/ban-types
  translateParams: {};
  phoneRegex = PHONE_REGEX;
  idNumberRegex = ID_NUMBER_REGEX;
  REG_TYPES = REG_TYPES;

  inactiveContracts = false;
  overdueContracts = 0;
  validationMessages = {
    telephone: {
      required: "Contact.TelephoneRequired",
      pattern: "Contact.InvalidNumber"
    }
  };
  isPublic = false;
  canRegisterNewCustomer = false;
  canLogNewJob = false;
  // loggedIn: Observable<boolean>;
  jobId: string;
  customer: CustomerMasterDto;
  customers: CustomerMasterDto[] = [];
  customerJobs: JobSearchDto[] = [];
  jobSearchBusy = false;
  customerSearchBusy = false;
  searched = false;
  sparesLink = "";

  phoneMask = PHONE_MASK;
  idNumberMask = ID_NUMBER_MASK;

  customerColumns: TableColumn[] = [
    { field: "id", header: "Customer.CustomerID", class: "w-15" },
    { field: "contacts.0.firstName", header: "Contact.FirstName", class: "w-15 d-none d-md-table-cell"},
    { field: "contacts.0.lastName", header: "Contact.LastName", class: "w-15 d-none d-md-table-cell" },
    { field: "contacts.0", header: "Contact.Contact", class: "w-25 d-table-cell d-md-none" },
    { field: "companyName", header: "Customer.Company", class: "w-20" }, // For those that enabled this transaltion
    { field: "contacts.0.note", header: "Contact.Note", class: "w-20" }, // For those that enabled this transaltion
    { field: "contacts.0.mobile", header: "Contact.Mobile", class: "w-20 d-none d-md-table-cell"},
    { field: "addresses.0.postalCode", header: "Address.PostalCode", class: "w-10 d-none d-md-table-cell" },
    { field: "contacts.0.idNumber", productHeader: "CustomerIDNumberAlias", class: "w-20" },
    { field: "link", header: "", tdClass: 'text-right', static: true, class: "w-20" }
  ];
  columns: TableColumn[] = [];

  @ViewChild('customerSearchResult') customerSearchResult: ElementRef;

  get layoutName(): string {
    return `CustomerSearch`;
  }

  showAppliances: boolean = false;
  constructor(
    protected layoutService: WebLayoutService,
    private jobService: JobService,
    private appQuery: AppQuery,
    private productSettings: ProductSettingService,
    formBuilder: FormBuilder,
    private customerService: CustomerService,
    private dialog: MatDialog,
    public route: ActivatedRoute
  ) {
    super(layoutService, formBuilder);
    this.productSettings.$loading.pipe(
      filter(l => !l)
    ).subscribe(_ => {
      this.showAppliances = this.productSettings.booleanValue("JobLoggingCustomerAppliances");
      this.canLogNewJob = this.productSettings.booleanValue("JobLoggingPublic");
      this.canRegisterNewCustomer = this.productSettings.booleanValue("CustomerRegisterPublic");
    });
    this.translateParams =  { company: "" };
  }

  protected createForm() {
    this.form = this.formBuilder.group({

      // JOB/CUSTOMER REGISTRATION
      newJob: this.formBuilder.group({
        firstname: "",
        lastname: "",
        fullname: "",
        telephone: "", // [Validators.required, //Validators.pattern(this.phoneRegex)]
        idNumber: "", // [Validators.pattern(this.idNumberRegex)]],
        customerId: "",
        email: ["", Validators.email],
        serial: ""
      }),

      // JOB QUERY
      job: this.formBuilder.group({
        lastname: "",
        telephone: "", // , Validators.pattern(this.phoneRegex),  [Validators.required]
        idNumber: "", // [Validators.pattern(this.idNumberRegex)]],
        customerId: "",
        jobId: ""
      }),

      // PURE CUSTOMER REGISTRATION
      customerSearch: this.formBuilder.group({
        firstname: "",
        lastname: "",
        telephone: "",
      }),
    });
    super.createForm();
  }

  withCountrySelector: boolean;
  ngOnInit() {
    console.log(this.route.snapshot.queryParams);
    console.log(this.route.snapshot.params);
    console.log(this.route.snapshot.data);
    console.log(this.route.snapshot.url);


    super.ngOnInit();
    this.withCountrySelector = this.productSettings.booleanValue('PhoneInputInternational');
    if(this.withCountrySelector){
      this.phoneMask = "";
    } else {
      const useMask = this.productSettings.numericValue("PhoneMask") === 0;
      this.phoneMask = useMask ? (this.productSettings.stringValue("PhoneMask") || this.phoneMask) : "";
    }

    this.appQuery.$tenant.pipe(
      filter(t => !!t),
      switchMap(tenant => this.productSettings.getCompanyInfo()),
      catchError(e => {
        this.translateParams = { company: "" };
        return of(null);
      })
    )
    .subscribe(info => {
      this.translateParams = { company: info?.friendlyName || "" };
    });

    // Use Route Data to set 'Public' state
    this.route.data
      .pipe(map((data) => data["isPublic"]), filter(flag => flag))
      .subscribe(
        (isPublic: boolean) => {
          this.isPublic = isPublic;
        }
      );

    // Primary Column Filter using Layouts (secondary one uses translations)
    this.getFieldSettings()
    .pipe(catchError(err => { console.error("Error getting field settings", err); return of({}); }))
    .subscribe(settings => {
      // @todo: remove this when we have a proper way to set field settings
      this.fieldSettings = settings;
      this.columns = this.customerColumns.filter(c => this.showField(c.field));
    });
  }

  showField(field: string, defaultVisible: boolean = true) {
    if (this.fieldSettings?.[field]?.disabled === undefined) return defaultVisible; // default
    return this.fieldSettings?.[field]?.disabled === false ? true : false;
  }

  protected validate(full: boolean) {
    super.validate(full);
    this.customers = [];
    this.customer = null;
  }

  getJobs(customerId: string) {
    this.jobSearchBusy = true;
    this.customerJobs = [];
    this.jobService
      .getCustomerJobs(customerId)
      .subscribe(lst => {
        this.customerJobs = lst;
        this.jobSearchBusy = false;
      });
  }

  customerAppliances: MachineMasterDto[] = [];
  applianceSearchBusy = false;
  getAppliances(customerId: string) {
    this.customerAppliances = [];
    this.applianceSearchBusy = true;
    this.customerService.getCustomer(customerId).subscribe(c => {
      this.customerAppliances = c.machines;
      this.applianceSearchBusy = false;
    });
  }

  triggerSearch(type: string) {
    if ( type === REG_TYPES.JOB){
      const newJob = this.form.get("newJob");
      newJob.patchValue({
        firstname: toProperCase(newJob.value.firstname),
        lastname: toProperCase(newJob.value.lastname)
      });
      this.customerSearch(
        {
          firstname: newJob.value.firstname,
          lastname: newJob.value.lastname,
          telephone: newJob.value.telephone,
          idNumber: newJob.value.idNumber,
          customerId: newJob.value.customerId,
          fullname: newJob.value.fullname,
          email: newJob.value.email,
          serial: newJob.value.serial
        },
        false
        ).then(res => {
          // if (res)
          // this.scrollCustomerSearchResultsIntoView();
        });

    } else {
      const fields = this.form.get("customerSearch");
      fields.patchValue({
        firstname: toProperCase(fields.value.firstname),
        lastname: toProperCase(fields.value.lastname)
      });
      this.customerSearch(
        {
          firstname: fields.value.firstname,
          lastname: fields.value.lastname,
          telephone: fields.value.telephone,
        },
        false,
      ).then(res => {
        // if (res)
        // this.scrollCustomerSearchResultsIntoView();
      });
    }
    trackEvent('Job Logging', 'search_customer', this.isPublic ? 'public' : 'internal');
  }

  scrollCustomerSearchResultsIntoView(timeout = 100) {
    this.customerSearchResult.nativeElement.scrollIntoView({behavior: 'smooth'});
  }

  customerSearch(values: any, blur = false, cb: () => void = () => {}) {
    if (blur && this.appQuery.loginStatus === LoginStatus.False) {
      return Promise.resolve(false);
    }
    this.customers = [];
    this.customerJobs = [];
    this.customer = null;
    this.searched =
      values.telephone
      || values.idNumber
      || values.lastname
      || values.customerId
      || values.fullname
      || values.email
      || values.serial;
    if (!this.searched) {
      return Promise.resolve(false);
    }
    this.customerSearchBusy = true;
    if (this.appQuery.loginStatus === LoginStatus.True) {
      return this.customerService
        .standardCustomerSearch(
          values.telephone,
          values.idNumber,
          values.customerId,
          values.lastname,
          values.fullname,
          values.email,
          values.serial
        )
        .toPromise()
        .then(lst => {
          this.customers = lst.items;
          return true;
        }).finally(() => {
          this.customerSearchBusy = false;
        });
    } else {
      return this.customerService
        .publicCustomerSearch(
          values.firstname,
          values.lastname,
          values.telephone,
          values.email,
          values.serial,
          values.customerId
        )
        .toPromise()
        .then(lst => {
          this.customers = lst;
          return true;
        }).finally(() => {
          this.customerSearchBusy = false;
        });
    }
  }

  jobLink(): any[] {
    const grp = this.form.get("job");
    const value = grp.value;
    if (value.telephone && value.jobId) {
      return grp.valid
        ? [
            value.jobId,
            { check: value.telephone.replace(NON_DIGIT_AND_PLUS, ""), details: "true" }
          ]
        : [];
    }
    return null;
  }

  jobLinkRow(job: JobSearchDto): any[] {
    const grp = this.form.get("newJob");
    const value = grp.value;
    return this.appQuery.loginStatus === LoginStatus.True
      ? [job.jobId]
      : [
          job.jobId,
          { check: value.telephone.replace(NON_DIGIT_AND_PLUS, ""), details: "true" }
        ];
  }

  private doSelectCustomer(customer: CustomerMasterDto) {
    this.customer = customer;
    // this.searched = false;
    this.getJobs(customer.id);

    this.customerAppliances = [];
    if(this.showAppliances)
      this.getAppliances(customer.id);
    this.customerContracts = [];
    if(this.customer.contracts)
      this.getContracts(customer.id);
  }

  customerContracts: ContractDto[] = [];
  getContracts(customerId: string){
    this.customerService.getCustomerContracts(customerId).subscribe(lst => {
      this.customerContracts = lst;
    });
  }

  getContractType(machineId: string){
    const contract = this.customerContracts.find(c => machineId.startsWith(c.contractId));
    if(contract){
      return contract.typeId;
    }
    return null;
  }

  selectCustomer(customer: CustomerMasterDto, dlg: any) {
    if (customer.inactiveContracts || customer.overdueContracts > 0) {
      this.overdueContracts = customer.overdueContracts;
      this.inactiveContracts = customer.inactiveContracts;
      this.dialog
        .open(dlg)
        .afterClosed()
        .subscribe(ok => {
          if (ok) {
            this.doSelectCustomer(customer);
          }
        });
    } else {
      this.doSelectCustomer(customer);
    }
    return false;
  }

  createJob() {}

  getRouteParams() {
    return this.route.snapshot.queryParams;
  }

  getPassedInParams() {
    return {
      // Passed-in params
      ...(this.getRouteParams().sourceId ? { sourceId: this.getRouteParams().sourceId } : {}),
      ...(this.getRouteParams().modelId ? { modelId: this.getRouteParams().modelId } : {}),
      ...(this.getRouteParams().serialNumber ? { serialNumber: this.getRouteParams().serialNumber } : {}),
      ...(this.getRouteParams().machineField1 ? { machineField1: this.getRouteParams().machineField1 } : {}),
      ...(this.getRouteParams().machineField2 ? { machineField2: this.getRouteParams().machineField2 } : {}),
      ...(this.getRouteParams().machineField3 ? { machineField3: this.getRouteParams().machineField3 } : {}),
      ...(this.getRouteParams().machineField4 ? { machineField4: this.getRouteParams().machineField4 } : {}),
      ...(this.getRouteParams().machineField5 ? { machineField5: this.getRouteParams().machineField5 } : {}),
    }
  }

  /**
   * @TODO - add url params that may have been bassed in from external sources
   * params: modelId, serialNumber, warranty (machineField[1-5]?), etc (check elx spec)
   */
  createNewJobValues() {
    const values = this.form.get("newJob").value;
    // note: not catering for 'full name' form value here...
    return {
      firstname: values.firstname,
      lastname: values.lastname,
      telephone: values.telephone.replace(NON_DIGIT_AND_PLUS, ""),
      idNumber: values.idNumber,
      email: values.email,
      ...this.getPassedInParams()
    }
  }

  createNewCustomerValues() {
    const values = this.form.get('customerSearch').value;
    return {
      firstname: values.firstname,
      lastname: values.lastname,
      telephone: values.telephone.replace(NON_DIGIT_AND_PLUS, ""),
      ...this.getPassedInParams()
    }
  }

  existingCustomerJobValues(appliance?: MachineMasterDto) {
    return {
      customerId: this.customer.id,
      contactId: this.customer.contacts[0]?.contactId,
      addressId: this.customer.addresses[0]?.addressId,
      ...(appliance?.machineId ? { machineId: appliance.machineId } : {}),
      ...this.getPassedInParams()
    }
  }

  selectedCustomerJobValues(row: CustomerMasterDto) {
    return {
      customerId: row.id,
      contactId: row.contacts[0]?.contactId,
      addressId: row.addresses[0]?.addressId,
      ...this.getPassedInParams()
    }
  }

  returnToList(){
    // this.customers = [];
    this.customerJobs = [];
    this.customer = null;
  }
}
